import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateLeaveInput,
  LeaveBalance,
  LeavePreview,
  LeavePreviewInput,
  LeaveRequestView,
  LeaveTypeView,
  Paginated,
} from '@asyagrup/group-hub-contracts';
import { api } from '@/lib/api/client';

export const leaveKeys = {
  types: () => ['leave', 'types'] as const,
  balances: (employeeId?: string) => ['leave', 'balances', employeeId ?? 'me'] as const,
  requests: (params: Record<string, string>) => ['leave', 'requests', params] as const,
  request: (id: string | null) => ['leave', 'request', id] as const,
};

export function useLeaveTypes() {
  return useQuery({
    queryKey: leaveKeys.types(),
    queryFn: () => api.get<LeaveTypeView[]>('/leave/types'),
    staleTime: 5 * 60_000,
  });
}

export function useMyBalances() {
  return useQuery({
    queryKey: leaveKeys.balances(),
    queryFn: () => api.get<LeaveBalance[]>('/leave/balances'),
    retry: false,
  });
}

export function useLeaveRequests(params: Record<string, string>) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== ''));
  return useQuery({
    queryKey: leaveKeys.requests(clean as Record<string, string>),
    queryFn: () => {
      const qs = new URLSearchParams({ ...clean, perPage: '100' }).toString();
      return api.get<Paginated<LeaveRequestView>>(`/leave/requests?${qs}`);
    },
  });
}

/**
 * The request a notification names, asked for BY ID.
 *
 * Not looked up among the rows the screen happens to have: «Onaylarım» filters
 * both of its tabs on PENDING_APPROVAL, so a request decided between the alert
 * being sent and being read is in neither, and «İzinlerim» caps at 100 rows.
 * The server applies the very predicate the list applies and answers 404
 * outside it, so this asks the record's own door rather than guessing.
 *
 * `retry: false`: a 404 IS the answer — gone, or not this reader's row — and so
 * is the 400 a mangled `?izin=` earns from the uuid guard. Retrying either only
 * delays the sentence that explains it.
 *
 * `enabled` is the caller's, because the screen stops asking once it has acted
 * on the link; react-query keeps the answer under this key while it is off, so
 * the effect that opened the panel still sees it.
 */
export function useLeaveRequest(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: leaveKeys.request(id),
    // Encoded: this id comes from an address a person can edit, unlike the
    // ids handed over by rows already on screen.
    queryFn: () => api.get<LeaveRequestView>(`/leave/requests/${encodeURIComponent(id ?? '')}`),
    enabled: id !== null && enabled,
    retry: false,
  });
}

/**
 * Live preview. The client never computes working days — the holiday calendar
 * lives on the server, and what this returns is exactly what will be recorded.
 */
export function useLeavePreview(input: LeavePreviewInput | null) {
  return useQuery({
    queryKey: ['leave', 'preview', input],
    queryFn: () => api.post<LeavePreview>('/leave/preview', input),
    enabled: input !== null,
    retry: false,
  });
}

export function useCreateLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeaveInput) =>
      api.post<{ id: string; requestNumber: string }>('/leave/requests', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['leave'] }),
  });
}

export function useLeaveDecision(action: 'approve' | 'reject' | 'cancel') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      api.post<{ status: string }>(`/leave/requests/${id}/${action}`, { comment }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['leave'] }),
  });
}
